import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useSimulationWebSocket - WebSocket hook for Digital Twin simulation state streaming.
 *
 * Connects to the simulation server via WebSocket, receives real-time state updates,
 * and provides a `send` function for issuing commands (play, pause, inject_event, etc.).
 *
 * @param {string|null} serverUrl - WebSocket URL (e.g., 'ws://localhost:8765/ws/sim/abc')
 * @returns {{ state: object|null, connected: boolean, send: function, error: string|null }}
 */
export default function useSimulationWebSocket(serverUrl) {
    const [state, setState] = useState(null);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const wsRef = useRef(null);
    const reconnectTimer = useRef(null);

    const connect = useCallback(() => {
        if (!serverUrl) return;

        try {
            const ws = new WebSocket(serverUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                setConnected(true);
                setError(null);
            };

            ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'state') {
                        setState(msg.data);
                    }
                } catch (parseErr) {
                    console.error('[useSimulationWebSocket] Failed to parse message:', parseErr);
                }
            };

            ws.onclose = () => {
                setConnected(false);
                wsRef.current = null;
                // Auto-reconnect after 3 seconds
                reconnectTimer.current = setTimeout(connect, 3000);
            };

            ws.onerror = () => {
                setError('WebSocket connection failed');
                ws.close();
            };
        } catch (err) {
            setError(err.message);
        }
    }, [serverUrl]);

    useEffect(() => {
        connect();
        return () => {
            if (wsRef.current) wsRef.current.close();
            if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        };
    }, [connect]);

    const send = useCallback((action, payload = {}) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action, ...payload }));
        }
    }, []);

    return { state, connected, send, error };
}
